/**
 * groupPositions — shared utility for computing isFirstInGroup / isLastInGroup.
 *
 * Sections break contiguous task blocks:
 *   - Task before a section → isLast
 *   - Task after a section → isFirst
 *   - First task in the list → isFirst
 *   - Last visible task → isLast
 *
 * Returns parallel arrays of booleans indexing into the input items.
 *
 * Used by GroupedTaskList, DndTaskList, DateGroupedList, SimpleList,
 * VirtualizedList, LaterProjectsDndList and any other task list renderer
 * to ensure consistent border-radius / visual grouping across all views.
 */

import { ObjectType } from "@/db/schema"
import type { ViewItem } from "../hooks/useViewData"

export interface GroupPositions {
  firsts: boolean[]
  lasts: boolean[]
}

/**
 * Compute isFirstInGroup / isLastInGroup for a flat list of ViewItems.
 * Sections act as visual group separators.
 */
export function computeGroupPositions(items: ViewItem[]): GroupPositions {
  const len = items.length
  const firsts = new Array<boolean>(len)
  const lasts = new Array<boolean>(len)
  for (let i = 0; i < len; i++) {
    const isSection = items[i]!.task.type === ObjectType.Section
    if (isSection) {
      // For sections, isFirstInGroup means "no item above me" — i.e. no top margin needed.
      // This is true when the section is at position 0 or preceded by another section
      // (section stacking uses its own spacing).
      const prev = i > 0 ? items[i - 1] : null
      firsts[i] = !prev || prev.task.type === ObjectType.Section
      lasts[i] = true
      continue
    }
    // Check if previous item is section or doesn't exist → isFirst
    const prev = i > 0 ? items[i - 1] : null
    firsts[i] = !prev || prev.task.type === ObjectType.Section
    // Check if next item is section or doesn't exist → isLast
    const next = i < len - 1 ? items[i + 1] : null
    lasts[i] = !next || next.task.type === ObjectType.Section
  }
  return { firsts, lasts }
}
